import currencyJs from 'currency.js';
import * as React from 'react';
import { InputStyled } from '../TextInput';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { mergeTests } from '../../test-utilities/mergeTests';
import { useMemo } from 'react';

const defaultCurrencyInputProps: ICurrencyInputProps = {
  decimalScale: 2,
  value: null,
  max: 90071992547409.91,
  // https://currency.js.org/
  // "As long as your currencies are less than 2^53 (in cents) or 90,071,992,547,409.91, you should not see any problems."
};

const getInitialFormattedValue = ({
  decimalScale,
  value: inputValue,
  thousandSeparator,
  valueIsCents,
}: ICurrencyInputProps): string => {
  if (inputValue === null || inputValue === undefined) {
    return '';
  }
  const currencyValue = currencyJs(Math.abs(valueIsCents ? inputValue / 100 : inputValue), { precision: decimalScale });
  return thousandSeparator ? currencyValue.format() : currencyValue.toString();
};

const computeIsNegative = (value?: number | null): boolean => {
  return typeof value === 'number' ? value < 0 : false;
};

const computeDecimalScaleMultiplier = (scale: number | undefined = defaultCurrencyInputProps.decimalScale): number => {
  return scale === 0 ? 1 : Math.pow(10, -scale!);
};

const getFormattedValue = (props: ICurrencyInputProps, value: string): string => {
  const decimalScaleMultiplier = computeDecimalScaleMultiplier(props.decimalScale);
  const formatCurrency = currencyJs(value, { precision: props.decimalScale }).multiply(decimalScaleMultiplier);
  return props.thousandSeparator ? formatCurrency.format() : formatCurrency.toString();
};

const getValue = (props: ICurrencyInputProps, value: string): string => {
  const decimalScaleMultiplier = computeDecimalScaleMultiplier(props.decimalScale);
  if (value === '0') {
    return value;
  }
  const formatCurrency = currencyJs(value, { precision: props.decimalScale }).multiply(decimalScaleMultiplier);
  return formatCurrency.value ? formatCurrency.toString() : '';
};

const limitToMax = (props: ICurrencyInputProps, value: string) => {
  if (props.max) {
    const decimalScaleMultiplier = computeDecimalScaleMultiplier(props.decimalScale);
    const formatCurrency = currencyJs(value, { precision: props.decimalScale }).multiply(decimalScaleMultiplier);
    if (formatCurrency.value > props.max) value = value.substr(0, value.length - 1);
  }
  return value;
};

const getFloatValue = (value: string): number | null => {
  return value ? Number(value) : null;
};

const computeIsNegativeString = (props: ICurrencyInputProps, value: string): boolean => {
  return props.allowNegative ? value.includes('-') : false;
};

export interface ICurrencyInputProps extends IHasTestData {
  name?: string;
  allowNegative?: boolean;
  value?: number | null;
  thousandSeparator?: boolean;
  decimalScale?: number;
  disabled?: boolean;
  placeholder?: string;
  showErrorHighlight?: boolean;
  getInputRef?: (elem: HTMLInputElement) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (value: ICurrencyInputValues) => void;
  readOnly?: boolean;
  autoFocus?: boolean;
  valueIsCents?: boolean;
  max?: number;
  hasAppendIcon?: boolean;
}

export interface ICurrencyInputValues {
  floatValue: number | null;
  formattedValue: string;
  value: string;
}

export const CurrencyInput: React.FC<ICurrencyInputProps> = ({
  decimalScale = 2,
  value = null,
  max = 90071992547409.91,
  ...rest
}) => {
  const props = useMemo(() => ({ decimalScale, value, max, ...rest }), [decimalScale, value, max, rest]);

  const [formattedValue, setFormattedValue] = React.useState<string>(getInitialFormattedValue(props));
  const [isNegative, setIsNegative] = React.useState<boolean>(computeIsNegative(props.value));

  React.useEffect(() => {
    setFormattedValue(getInitialFormattedValue(props));
    setIsNegative(computeIsNegative(props.value));
  }, [props]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isNegative = computeIsNegativeString(props, e.target.value);
    const inputDigits = e.target.value.replace(/[^\d]/g, '');
    const numberString = limitToMax(props, inputDigits);
    const value = getValue(props, numberString);
    const formatted = value === '' ? value : getFormattedValue(props, numberString);
    const floatValue = getFloatValue(value);

    setIsNegative(isNegative);
    setFormattedValue(formatted);

    if (props.onChange) {
      props.onChange({
        floatValue: isNegative && floatValue !== null ? -floatValue : floatValue,
        formattedValue: isNegative ? `-${formatted}` : formatted,
        value: isNegative ? `-${value}` : value,
      });
    }
  };

  return (
    <InputStyled
      name={props.name}
      ref={props.getInputRef}
      placeholder={props.placeholder}
      disabled={props.disabled}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      showErrorHighlight={!!props.showErrorHighlight}
      value={isNegative ? `-${formattedValue}` : formattedValue}
      onChange={onChange}
      readOnly={props.readOnly}
      autoFocus={props.autoFocus}
      data-test-id={mergeTests(props['data-test-id'], 'input')}
      data-test-class={props['data-test-class']}
      hasAppendIcon={props.hasAppendIcon}
    />
  );
};
